export type AchievementLanguage = 'en' | 'sk' | 'cs';
export type AchievementSort = 'order' | 'most' | 'least';

export interface AdminAchievementItem {
  id: string;
  inCode: boolean;
  inDatabase: boolean;
  enabled: boolean;
  category: string;
  displayOrder: number;
  criteriaVersion: number;
  title: Record<AchievementLanguage, string> | null;
  description: Record<AchievementLanguage, string> | null;
  groupTitle: string | null;
  layout: string | null;
  reward: { kind: string; collectibleId: string | null };
  requirements: { metric: string; target: number }[];
  unlocks: {
    total: number;
    last7Days: number;
    last30Days: number;
    firstAt: string | null;
    lastAt: string | null;
  };
  drift: string[];
}

export interface AdminAchievementOverview {
  generatedAt: string;
  readers: number;
  items: AdminAchievementItem[];
}

const metricLabels: Record<string, string> = {
  activityDayCount: 'Reading days',
  activityWeekCount: 'Active weeks',
  completedWorkCount: 'Finished works',
  distinctGenreCount: 'Genres finished',
  firstImpressionCount: 'Finished books rated',
  forwardSessionCount: 'Progress logs',
  libraryBookCount: 'Books in library',
  maxBookActivityDays: 'Days on one book',
  postFinishRatingCount: 'Ratings after finishing',
  publicReviewWorkCount: 'Approved public reviews',
  ratedWorkCount: 'Books rated',
  readingRitualProgress: 'Ritual steps',
  trackedCompletedWorkCount: 'Tracked finishes',
};

/** Unknown metrics fall back to their raw name so new rules still show up honestly. */
export function metricLabel(metric: string): string {
  return metricLabels[metric] ?? metric;
}

export function ruleSummary(item: AdminAchievementItem): string {
  if (!item.requirements.length) return 'No rule';
  return item.requirements
    .map(({ metric, target }) => `${metricLabel(metric)} ≥ ${target}`)
    .join(' and ');
}

export function rewardSummary(reward: AdminAchievementItem['reward']): string {
  const labels: Record<string, string> = {
    none: 'No reward',
    badge: 'Badge',
    theme_pack: 'Theme pack',
    plant: 'Plant',
    pot: 'Pot',
  };
  return labels[reward.kind] ?? reward.kind;
}

/** Share of all registered readers who earned it, rounded to one decimal place. */
export function earnedShare(unlocks: number, readers: number): number {
  if (readers <= 0) return 0;
  return Math.round((unlocks / readers) * 1000) / 10;
}

export function summarizeAchievements(overview: AdminAchievementOverview) {
  return {
    readers: overview.readers,
    live: overview.items.filter((item) => item.enabled && item.inCode && item.inDatabase).length,
    total: overview.items.length,
    unlocks: overview.items.reduce((sum, item) => sum + item.unlocks.total, 0),
    unlocksLast7Days: overview.items.reduce((sum, item) => sum + item.unlocks.last7Days, 0),
    needsAttention: overview.items.filter((item) => item.drift.length > 0).length,
  };
}

export function sortAchievements(
  items: readonly AdminAchievementItem[],
  sort: AchievementSort,
): AdminAchievementItem[] {
  const byOrder = (left: AdminAchievementItem, right: AdminAchievementItem) =>
    left.displayOrder - right.displayOrder || left.id.localeCompare(right.id);
  return [...items].sort((left, right) =>
    sort === 'order'
      ? byOrder(left, right)
      : (sort === 'most'
          ? right.unlocks.total - left.unlocks.total
          : left.unlocks.total - right.unlocks.total) || byOrder(left, right),
  );
}
